import { useEffect, useState } from 'react';
import { AlertCircle, Handshake, Loader2, Percent, Save, Trash2, X } from 'lucide-react';
import { toast } from 'sonner';
import { ReferralSearchSelect } from './ReferralSearchSelect';
import { QuickReferralModal } from './QuickReferralModal';
import { ValidatedNumberInput } from './validated-number-input';
import { useUpdateAdmission } from '../../services/httpServices/queries';
import type { Admission } from '../../services/httpServices/ipdService';

interface AdmissionReferralModalProps {
  open: boolean;
  admission: Admission;
  onClose: () => void;
  /**
   * Clears the referral. The parent owns the destructive confirmation dialog
   * and the mutation, so it can also refresh the page-level summary.
   */
  onRequestRemove?: () => void;
}

/**
 * Focused edit dialog for an admission's referral payout: picks the referral
 * partner and the flat commission for the stay. Everything else on the
 * admission is managed inline (transfer, charges, discharge), so this replaces
 * the old full-page admission edit form.
 */
export function AdmissionReferralModal({
  open,
  admission,
  onClose,
  onRequestRemove,
}: AdmissionReferralModalProps) {
  const updateAdmission = useUpdateAdmission();
  const [referralId, setReferralId] = useState('');
  const [referralLabel, setReferralLabel] = useState('');
  // Kind filter for the partner search (the partner itself carries the type).
  const [referralTypeFilter, setReferralTypeFilter] = useState<
    'ALL' | 'DOCTOR' | 'EMPLOYEE' | 'OTHER'
  >('ALL');
  const [flatAmount, setFlatAmount] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [showQuickReferral, setShowQuickReferral] = useState(false);

  // Seed from the saved admission every time the dialog opens. Deliberately not
  // keyed on the admission object: a background refetch must not overwrite the
  // values being edited.
  useEffect(() => {
    if (!open) return;
    setReferralId(admission.referralId || '');
    setReferralLabel(admission.referral?.name || '');
    setReferralTypeFilter(admission.referral?.type || 'ALL');
    setFlatAmount(Math.round(Number(admission.referralFlatAmount) || 0));
    setError(null);
    setShowQuickReferral(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Close on Escape, matching the other dialogs in the app.
  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !updateAdmission.isPending) onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, updateAdmission.isPending, onClose]);

  if (!open) return null;

  // Referral payouts are whole taka — no paisa in the payout ledger.
  const amount = Math.max(0, Math.round(Number(flatAmount) || 0));

  const handleSave = () => {
    setError(null);
    if (!referralId && amount > 0) {
      setError('Select a referral partner for this commission.');
      return;
    }
    if (referralId && amount <= 0) {
      setError('Enter a commission amount greater than 0, or remove the referral.');
      return;
    }

    updateAdmission.mutate(
      {
        id: admission.id,
        data: {
          referralId: referralId || null,
          referralFlatAmount: referralId ? amount : 0,
        },
      },
      {
        onSuccess: () => {
          toast.success(referralId ? 'Referral saved' : 'Referral removed');
          onClose();
        },
        onError: (err: any) => {
          setError(
            err?.response?.data?.message ||
              'Failed to save the referral. Please try again.',
          );
        },
      },
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm modal-overlay-enter">
      <div className="flex max-h-[88vh] w-full max-w-2xl flex-col rounded-2xl border border-border bg-surface shadow-2xl modal-panel-enter">
        {/* Header */}
        <div className="flex shrink-0 items-center justify-between gap-3 rounded-t-2xl border-b border-border bg-background/50 px-4 py-3">
          <div className="flex min-w-0 items-center gap-3">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/10">
              <Handshake size={18} className="text-primary" />
            </div>
            <div className="min-w-0">
              <h2 className="truncate text-base font-semibold text-text">Referral</h2>
              <p className="truncate text-xs text-text-muted">
                Attach a referrer and flat commission to this admission
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={updateAdmission.isPending}
            aria-label="Close"
            className="shrink-0 rounded-lg p-1.5 transition-colors hover:bg-background disabled:opacity-50"
          >
            <X size={18} className="text-text-muted" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 space-y-4 overflow-y-auto p-4">
          {error && (
            <div className="flex items-start gap-2 rounded-lg border border-danger/20 bg-danger/10 p-2.5 text-sm text-danger">
              <AlertCircle size={15} className="mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {/* Type, partner and amount share one row, matching the admission form. */}
          <div className="grid grid-cols-1 items-end gap-3 sm:grid-cols-[110px_minmax(0,1fr)_140px]">
            <div>
              <label
                htmlFor="admission-referral-type"
                className="mb-1 block text-[0.625rem] font-medium uppercase tracking-wide text-text-muted"
              >
                Type
              </label>
              <select
                id="admission-referral-type"
                value={referralTypeFilter}
                onChange={(e) =>
                  setReferralTypeFilter(
                    e.target.value as 'ALL' | 'DOCTOR' | 'EMPLOYEE' | 'OTHER',
                  )
                }
                className="w-full rounded-lg border border-border bg-background px-2 py-2 text-sm text-text transition-colors focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="ALL">All</option>
                <option value="DOCTOR">Doctor</option>
                <option value="EMPLOYEE">Employee</option>
                <option value="OTHER">Other</option>
              </select>
            </div>

            <div className="min-w-0">
              <label className="mb-1 block text-[0.625rem] font-medium uppercase tracking-wide text-text-muted">
                Referral partner
              </label>
              <ReferralSearchSelect
                value={referralId}
                selectedLabel={referralLabel}
                type={referralTypeFilter}
                onChange={(id, partner) => {
                  setReferralId(id);
                  setReferralLabel(partner?.name || '');
                  if (!id) setFlatAmount(0);
                  setError(null);
                }}
                onAddNew={() => setShowQuickReferral(true)}
                addNewLabel="Add new referral"
                placeholder="Search referral by name / mobile…"
              />
            </div>

            <div>
              <label
                htmlFor="admission-referral-amount"
                className="mb-1 flex items-center gap-1 text-[0.625rem] font-medium uppercase tracking-wide text-text-muted"
              >
                <Percent size={11} />
                Amount (৳)
              </label>
              <ValidatedNumberInput
                id="admission-referral-amount"
                value={flatAmount}
                onChange={(next) => {
                  setFlatAmount(Math.round(next));
                  setError(null);
                }}
                disabled={!referralId}
                decimals={0}
                placeholder="0"
                className="w-full rounded-lg border border-border bg-background px-2.5 py-2 text-sm text-text transition-colors focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-60"
              />
            </div>
          </div>

          <p className="text-[0.6875rem] leading-relaxed text-text-muted">
            A one-off amount paid to the referrer for this stay — not a percentage of the bill.
            Use <span className="font-semibold text-danger">Remove</span> to unlink the
            referrer from this admission entirely.
          </p>
        </div>

        {/* Footer */}
        <div className="flex shrink-0 items-center justify-end gap-2 rounded-b-2xl border-t border-border bg-background/50 px-4 py-3">
          {admission.referralId && onRequestRemove && (
            <button
              type="button"
              onClick={onRequestRemove}
              disabled={updateAdmission.isPending}
              title="Remove the referral partner and its commission"
              className="mr-auto inline-flex items-center gap-1.5 rounded-lg px-2.5 py-2 text-sm font-medium text-danger transition-colors hover:bg-danger/10 disabled:opacity-50"
            >
              <Trash2 size={16} />
              Remove
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            disabled={updateAdmission.isPending}
            className="flex items-center gap-2 px-3.5 py-2 text-sm text-text-muted transition-colors hover:text-text disabled:opacity-50"
          >
            <X size={16} />
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={updateAdmission.isPending}
            className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-dark disabled:opacity-50"
          >
            {updateAdmission.isPending ? (
              <Loader2 size={16} className="animate-spin" />
            ) : (
              <Save size={16} />
            )}
            Save Referral
          </button>
        </div>
      </div>

      <QuickReferralModal
        open={showQuickReferral}
        onClose={() => setShowQuickReferral(false)}
        onCreated={(partner) => {
          setShowQuickReferral(false);
          setReferralId(partner.id);
          setReferralLabel(partner.name);
          setError(null);
        }}
      />
    </div>
  );
}
